import type { AttachmentType } from '@/entities/consignments';

/**
 * Where a document in the receipt modal came from.
 *
 * Files hang off the shipment they were uploaded with, and a receive can be
 * partial, so the two groups are genuinely different things: `SHIPMENT` is the
 * paperwork the dispatching side attached to the incoming shipment, `RECEIPT`
 * is what an earlier receipt against it already recorded. JSF shows only the
 * first group, which is why its users re-upload the same waybill on every
 * partial receive.
 */
export const RECEIVE_ATTACHMENT_SOURCE = {
  SHIPMENT: 'SHIPMENT',
  RECEIPT: 'RECEIPT',
  /** Stored against a release by Receive and Release. */
  RELEASE: 'RELEASE'
} as const;

export type ReceiveAttachmentSource = (typeof RECEIVE_ATTACHMENT_SOURCE)[keyof typeof RECEIVE_ATTACHMENT_SOURCE];

/**
 * Attachment types a receipt may carry — `GO` (GRN-Out) belongs to release and
 * is rejected here. The context call reports the same list in
 * `allowedAttachmentTypes`; this is the fallback for screens that have no
 * context call of their own, such as Receive History.
 */
export const RECEIVE_ATTACHMENT_TYPES: AttachmentType[] = ['WB', 'PL', 'GI', 'PC', 'LR', 'OT'];

/** The release set — `GO` (GRN-Out) in place of `GI`. Fallback when the release context does not list its own. */
export const RELEASE_ATTACHMENT_TYPES: AttachmentType[] = ['WB', 'PL', 'GO', 'PC', 'LR', 'OT'];

/** Attachment metadata — never the bytes, which come from the download endpoint. */
export interface ReceiveAttachmentDto {
  id: number;
  /**
   * The shipment the file hangs off — a receipt for `RECEIPT` items, so it is
   * **not** interchangeable with the shipment being received. Goes into the
   * download path verbatim.
   */
  shipmentId: string;
  source: ReceiveAttachmentSource;
  fileName: string;
  contentType: string;
  fileSize: number;
  attachmentType: AttachmentType;
  comment?: string | null;
}

/** A file picked in the modal but not sent yet. */
export interface ReceivePendingAttachment {
  tempId: string;
  file: File;
  attachmentType: AttachmentType;
}

/** `GET /shipments/{shipmentId}/attachments/{attachmentId}` — the file itself. */
export interface ReceiveAttachmentDownloadArgs {
  /** `attachments[].shipmentId`, not the shipment being received. */
  shipmentId: string;
  attachmentId: number;
  projectId?: number;
  siteId?: number;
}

/** `POST /shipments/{shipmentId}/attachments` — add documents to a booked receipt. */
export interface ReceiveAttachmentUploadArgs {
  /** Must be a receipt (shipment type `R`); the backend rejects anything else. */
  shipmentId: string;
  projectId?: number;
  siteId?: number;
  files: File[];
  /** One per file, in the same order — the backend accepts that or a single type for all. */
  attachmentTypes: AttachmentType[];
  /** Stored on every file of this upload; the API has no per-file comment. */
  comment?: string;
}
